import type { BlockWireframe } from '@octopus-do/octopus-project-core'
import { TinyPopover, Toolbar } from '@octopus-do/octopus-storybook'
import { Wireframe } from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { forwardRef } from 'react'

import { FREE_WIREFRAMES, WIREFRAME_GROUPS } from '@/constants/wireframes'
import { useWireframesMenu } from './hooks'

const WIDTH = 210
// As the former popup: 4px from the owner's edge, its top 40px above the owner's top.
const GAP_X = 4
const GAP_Y = -40

type Side = 'left' | 'right'

interface TriggerProps {
  disabled: boolean
  onOpen: () => void
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ disabled, onOpen }, ref) => {
    const { active, toggle } = TinyPopover.useTrigger()

    const onClick = () => {
      if (!active) {
        onOpen()
      }

      toggle()
    }

    return (
      <Toolbar.DropdownButton
        active={active}
        disabled={disabled}
        icon={Wireframe}
        onClick={onClick}
        ref={ref}
        tooltip="Wireframes"
      />
    )
  }
)

interface Props {
  // The block or the symbol card the popover sits next to.
  anchorRef: React.RefObject<HTMLDivElement | null>
  disabled?: boolean
  // The owner's wireframes as a whole after a pick or Clear.
  onChange: (wireframes: BlockWireframe[]) => void
  // The popover replaces the owner's toolbar as the former popup did: the
  // toolbar hides between these two.
  onClose: () => void
  onOpen: () => void
  // Right of a block on the artboard, left of a card in the sidebar; flips when there is no room.
  side: Side
  wireframes: BlockWireframe[]
}

// The wireframes popover of a block or a symbol card: the catalog with a
// search; with Select multiple on (or Shift held) a pick adds instead of
// replacing. A free plan keeps the free ones and sells the plan for the rest;
// Request opens the feedback form.
export const WireframesMenu: React.FC<Props> = ({
  anchorRef,
  disabled = false,
  onChange,
  onClose,
  onOpen,
  side,
  wireframes,
}) => {
  const menu = useWireframesMenu(wireframes, onChange)

  return (
    <TinyPopover.Root
      align="start"
      alignOffset={GAP_Y}
      anchorRef={anchorRef}
      content={TinyPopover.Wireframes}
      free={FREE_WIREFRAMES}
      groups={WIREFRAME_GROUPS}
      locked={menu.locked}
      multiple={menu.multiple}
      onClear={menu.onClear}
      onClose={onClose}
      onMultipleChange={menu.onMultipleChange}
      onPick={menu.onPick}
      onRequest={menu.onRequest}
      onUpgrade={menu.onUpgrade}
      request={menu.request}
      selected={wireframes}
      side={side}
      sideOffset={GAP_X}
      width={WIDTH}
    >
      <Trigger disabled={disabled} onOpen={onOpen} />
    </TinyPopover.Root>
  )
}
